import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { Feature } from "@/services/types";

/**
 * App-wide trigger for the single shared Upgrade prompt. Any gated surface calls
 * {@link UpgradePromptApi.requestUpgrade} to ask the user to upgrade; the actual modal UI is
 * mounted once at the app root (see UpgradePromptHost, wired in Milestone 3) and subscribes to
 * this context. Keeping one trigger avoids duplicate upgrade dialogs across the app.
 */
export interface UpgradePromptState {
  open: boolean;
  /** The feature that prompted the upgrade, if any (lets the modal tailor its copy). */
  feature: Feature | null;
}

export interface UpgradePromptApi extends UpgradePromptState {
  /** Open the upgrade prompt, optionally noting which feature was requested. */
  requestUpgrade: (feature?: Feature | null) => void;
  /** Close the upgrade prompt. */
  dismiss: () => void;
}

const UpgradePromptContext = createContext<UpgradePromptApi | null>(null);

export function UpgradePromptProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UpgradePromptState>({ open: false, feature: null });

  const requestUpgrade = useCallback((feature?: Feature | null) => {
    setState({ open: true, feature: feature ?? null });
  }, []);

  const dismiss = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const value = useMemo<UpgradePromptApi>(
    () => ({ ...state, requestUpgrade, dismiss }),
    [state, requestUpgrade, dismiss]
  );

  return <UpgradePromptContext.Provider value={value}>{children}</UpgradePromptContext.Provider>;
}

/**
 * Access the shared upgrade prompt. Safe to call outside the provider — returns a no-op
 * trigger so a gated component never crashes if the host isn't mounted (defensive; the
 * provider is mounted at the app root).
 */
export function useUpgradePrompt(): UpgradePromptApi {
  const ctx = useContext(UpgradePromptContext);
  if (!ctx) {
    return { open: false, feature: null, requestUpgrade: () => {}, dismiss: () => {} };
  }
  return ctx;
}
